<script setup>
// ============================================================================
// BonBoZ: NGUỒN QUẢNG CÁO ở đáy tab Thông tin — khách này đến từ quảng cáo nào.
//
// Cùng nội dung với thẻ quảng cáo trong khung chat (`bubbles/AdCard.vue`) nhưng
// không tái dụng được component đó: nó buộc vào `useMessageContext()` và rộng
// cố định 330px, trong khi sidebar chỉ ~300px. Dùng chung DỮ LIỆU (helper
// `adSource.js`), không dùng chung khung.
// ============================================================================
import { computed, ref, watch } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { pickAdSource } from 'dashboard/helper/adSource';
import AccordionItem from 'dashboard/components/Accordion/AccordionItem.vue';
import Icon from 'next/icon/Icon.vue';

const props = defineProps({
  contact: { type: Object, default: () => ({}) },
});

const chat = useMapGetter('getSelectedChat');

const ad = computed(() =>
  pickAdSource(
    chat.value?.messages,
    chat.value?.custom_attributes,
    props.contact?.custom_attributes
  )
);

const expanded = ref(false);

// Cung ly do voi bubbles/AdCard.vue: URL fbcdn co han, khong bat loi thi trinh
// duyet ve icon anh vo. O sidebar con te hon vi o anh cao 112px co dinh — anh
// hong de lai dung mot mang xam giua panel.
//
// PHAI reset khi doi anh: component nay song xuyen cac hoi thoai (sidebar
// khong unmount khi chuyen chat), nen mot lan loi ma khong reset thi MOI
// hoi thoai sau do deu hien "khong tai duoc" du anh cua chung con song.
const anhHong = ref(false);
watch(
  () => ad.value?.image_url,
  () => {
    anhHong.value = false;
    expanded.value = false;
  }
);

// Accordion nằm TRONG component chứ không ở ContactPanel: khách không đến từ
// quảng cáo thì cả tiêu đề cũng không hiện, thay vì một mục rỗng bấm vào trống.
const { isContactSidebarItemOpen, toggleSidebarUIState } = useUISettings();
const KEY = 'is_bonboz_ad_source_open';
</script>

<template>
  <AccordionItem
    v-if="ad"
    :title="$t('BONBOZ.CRM.ADS_TITLE')"
    :is-open="isContactSidebarItemOpen(KEY)"
    compact
    @toggle="() => toggleSidebarUIState(KEY)"
  >
    <div class="flex flex-col gap-2 px-1 pb-2 text-sm">
      <!-- BonBoZ: NOI RO khi ma quang cao chi den tu ho so khach.
           `stamp_contact` giu ma DAU TIEN dua khach toi, vinh vien. Nen mot
           khach tung bam quang cao mot lan thi MOI hoi thoai ve sau cua ho deu
           doc ra co nguon — ke ca hoi thoai ho tu nhan. Bo loc "tu quang cao"
           truoc day cung xet dau nay nen tra ve ca hoi thoai khong co quang
           cao; tu 28/08/2026 bo loc chi xet muc HOI THOAI (quick_filter.rb),
           con panel nay van doc dau contact de ke lich su khach. -->
      <p
        v-if="ad.chi_o_contact"
        class="px-2 py-1.5 mb-0 text-xs rounded-md bg-n-alpha-1 text-n-slate-11"
      >
        {{ $t('BONBOZ.CRM.ADS_CHI_O_KHACH') }}
      </p>
      <p v-if="ad.title" class="font-medium text-n-slate-12">{{ ad.title }}</p>

      <img
        v-if="ad.image_url && !anhHong"
        :src="ad.image_url"
        alt=""
        loading="lazy"
        decoding="async"
        class="object-cover w-full rounded-lg h-28"
        @error="anhHong = true"
      />
      <div
        v-else-if="ad.image_url"
        class="flex gap-1.5 justify-center items-center px-2 rounded-lg h-14 text-xs text-center text-n-slate-11 bg-n-alpha-black1"
      >
        <Icon icon="i-lucide-image-off" class="size-4 shrink-0" />
        {{ $t('BONBOZ.TIN_NHAN.AD_ANH_HET_HAN') }}
      </div>

      <p
        v-if="ad.body"
        class="whitespace-pre-line text-n-slate-11"
        :class="expanded ? '' : 'line-clamp-4'"
      >
        {{ ad.body }}
      </p>
      <button
        v-if="ad.body && ad.body.length > 160"
        class="self-start text-xs font-medium text-n-blue-11"
        @click="expanded = !expanded"
      >
        {{
          expanded
            ? $t('BONBOZ.TIN_NHAN.THU_GON')
            : $t('BONBOZ.TIN_NHAN.XEM_THEM')
        }}
      </button>

      <dl class="grid grid-cols-[auto_minmax(0,1fr)] gap-x-2 gap-y-1 text-xs">
        <dt class="text-n-slate-10">{{ $t('BONBOZ.CRM.ADS_AD_ID') }}</dt>
        <dd class="m-0 tabular-nums break-all text-n-slate-12">
          {{ ad.ad_id }}
        </dd>

        <template v-if="ad.source">
          <dt class="text-n-slate-10">{{ $t('BONBOZ.CRM.ADS_SOURCE') }}</dt>
          <dd class="m-0 break-all text-n-slate-12">{{ ad.source }}</dd>
        </template>

        <template v-if="ad.ref">
          <dt class="text-n-slate-10">{{ $t('BONBOZ.CRM.ADS_REF') }}</dt>
          <dd class="m-0 break-all text-n-slate-12">{{ ad.ref }}</dd>
        </template>
      </dl>

      <a
        v-if="ad.permalink_url"
        :href="ad.permalink_url"
        target="_blank"
        rel="noopener noreferrer"
        class="inline-flex items-center gap-1 text-xs font-medium text-n-blue-11"
      >
        <Icon icon="i-lucide-external-link" class="size-3.5" />
        {{ $t('BONBOZ.CRM.ADS_VIEW_POST') }}
      </a>
    </div>
  </AccordionItem>
</template>
